import { BaseDialog, Button, HookedForm } from '@app/components';
import { BusinessContacts } from './BusinessContacts';
import { InputField } from '@app/components/Fields/InputField';
import { PhoneField } from '@app/components/Fields/PhoneField';
import { SelectField } from '@app/components/Fields/SelectField';
import { TextField } from '@app/components/Fields/TextField';
import { MultipleSelectField } from '@app/components/Fields/MultipleSelectField';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { fetchBusinessCategories } from '@app/redux/features/business';
import {
  getBusinessInfo,
  getCategories,
  updateBusiness,
  uploadBusinessPhoto,
} from '@app/services/business';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { CATEGORY_SUBCATEGORIES } from '@app/utils/consts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import PencilIcon from '@app/assets/icons/pencil.svg?react';
import NoLogo from '@app/assets/images/no-logo.png';
import SuitcaseIcon from '@app/assets/icons/suitcase.svg?react';
import { usePermissions } from '@app/contexts/PermissionContext';

type BusinessFormValues = {
  logo?: File | null;
  name: string;
  email: string;
  phoneNumber: string;
  websiteUrl: string;
  address: string;
  categoryId: string;
  subcategory: string[];
  description: string;
};

export const BusinessDetails = () => {
  const dispatch = useAppDispatch();
  const businessState = useAppSelector((state) => state.business);
  const { can } = usePermissions();
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | undefined>();
  const [pendingPhoto, setPendingPhoto] = useState<File | undefined>();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  useEffect(() => {
    const categories = businessState?.categories ?? [];
    if (!businessState?.isCategoriesFetched && categories.length === 0) {
      dispatch(fetchBusinessCategories());
    }
  }, [
    businessState?.isCategoriesFetched,
    businessState?.categories?.length,
    dispatch,
  ]);

  const defaultValues: BusinessFormValues = {
    logo: null,
    name: businessState.selected?.name ?? '',
    email: '',
    phoneNumber: '',
    websiteUrl: '',
    address: '',
    categoryId: '',
    subcategory: [],
    description: '',
  };

  const [display, setDisplay] = useState<BusinessFormValues>(defaultValues);
  const form = useForm<BusinessFormValues>({ defaultValues: display });

  const { data: categoriesResponse } = useQuery({
    queryKey: ['business-categories'],
    queryFn: () => getCategories(),
  });

  const { data: businessInfoResponse } = useQuery({
    queryKey: ['business-info'],
    queryFn: () => getBusinessInfo(),
  });

  const selectedCategoryLabel = useMemo(() => {
    const categories =
      (categoriesResponse?.categories ?? businessState?.categories) || [];
    const found = categories.find(
      (c: { id: string; name: string }) => c.id === display.categoryId
    );
    return found?.name ?? '';
  }, [categoriesResponse, businessState?.categories, display.categoryId]);

  useEffect(() => {
    // Initialize selectedCategoryId from current display when categories load
    if (!selectedCategoryId && display.categoryId) {
      setSelectedCategoryId(display.categoryId);
    }
  }, [display.categoryId, selectedCategoryId]);

  useEffect(() => {
    const info = (businessInfoResponse as any)?.data;
    if (!info) return;
    setDisplay({
      logo: null,
      name: info.name ?? '',
      email: info.email ?? '',
      phoneNumber: info.phoneNumber ?? '',
      websiteUrl: info.websiteUrl ?? '',
      address: info.address ?? '',
      categoryId: info.categoryId ?? '',
      subcategory: info.subcategory
        ? String(info.subcategory)
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
        : [],
      description: info.description ?? '',
    });
    if (info?.photo?.url) setLogoPreview(info.photo.url as string);
    if (info?.categoryId) setSelectedCategoryId(info.categoryId as string);
  }, [businessInfoResponse]);

  useEffect(() => {
    // Keep form in sync when display values change (e.g., after fetch)
    form.reset(display);
  }, [display, form]);

  const subcategoryOptions = useMemo(() => {
    const categories =
      (categoriesResponse?.categories ?? businessState?.categories) || [];
    const selectedName = categories.find(
      (c: { id: string; name: string }) => c.id === selectedCategoryId
    )?.name;
    const list = selectedName
      ? (CATEGORY_SUBCATEGORIES[selectedName] ?? [])
      : [];
    return list.map((s) => ({ value: s, label: s }));
  }, [selectedCategoryId, categoriesResponse, businessState?.categories]);

  const { mutateAsync: mutateUpdateBusiness, isPending: isUpdating } =
    useMutation({
      mutationKey: ['update-business'],
      mutationFn: (payload: Partial<BusinessFormValues>) =>
        updateBusiness({
          name: payload.name,
          email: payload.email,
          phoneNumber: payload.phoneNumber,
          websiteUrl: payload.websiteUrl,
          // Send as comma-separated string for compatibility
          subcategory: Array.isArray(payload.subcategory)
            ? (payload.subcategory as string[]).join(', ')
            : (payload.subcategory as unknown as string),
          address: payload.address,
          description: payload.description,
          categoryId: payload.categoryId,
        }),
      onError: (error: any) => {
        const message = error?.data?.message || error?.response?.data?.message;
        if (message?.includes('phone number already exists')) {
          toast.error('This phone number is already used by another business');
        } else {
          toast.error('Failed to update business info');
        }
      },
    });

  const { mutateAsync: mutateUploadPhoto, isPending: isUploadingPhoto } =
    useMutation({
      mutationKey: ['upload-business-photo'],
      mutationFn: (file: File) => uploadBusinessPhoto(file),
      onError: () => toast.error('Failed to upload photo'),
    });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full"
    >
      <div className="w-full p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-base-ghostWhite">
            <div className="p-6 flex items-start justify-between">
              <div>
                <h5 className="font-bold mb-1">Main info</h5>
                <p className="text-sm text-base-frenchGray">
                  This data will be used for campaigns.
                </p>
              </div>

              {can('editBusinessInfo') && (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="p-2 hover:bg-base-seasalt rounded-lg"
                  aria-label="Edit business details"
                >
                  <PencilIcon />
                </button>
              )}
            </div>
            <div className="px-6">
              <hr className="border-base-ghostWhite" />
            </div>

            <div className="p-6 space-y-6">
              <div className="flex gap-4 items-start">
                <div className="h-[68px] w-[68px] rounded-lg overflow-hidden bg-base-seasalt flex items-center justify-center">
                  {(() => {
                    const src =
                      logoPreview ||
                      (businessInfoResponse as any)?.data?.photo?.url ||
                      (NoLogo as string);
                    return (
                      <img src={src} className="w-full h-full object-cover" />
                    );
                  })()}
                </div>
              </div>
              <hr className="border-base-ghostWhite" />
              <div className="grid grid-cols-1 gap-6">
                <div className="grid grid-cols-[180px_1fr] items-start gap-4">
                  <p className="uppercase text-xs text-base-frenchGray font-semibold">
                    Business name
                  </p>
                  <p className="text-base-raisinBlack">{display.name || '—'}</p>
                </div>
                <div className="grid grid-cols-[180px_1fr] items-start gap-4">
                  <p className="uppercase text-xs text-base-frenchGray font-semibold">
                    Website URL
                  </p>
                  <p className="text-base-raisinBlack">
                    {display.websiteUrl || '—'}
                  </p>
                </div>
                <div className="grid grid-cols-[180px_1fr] items-start gap-4">
                  <p className="uppercase text-xs text-base-frenchGray font-semibold">
                    Business address
                  </p>
                  <p className="text-base-raisinBlack">
                    {display.address || '—'}
                  </p>
                </div>
                <hr className="border-base-ghostWhite" />
              </div>

              <div className="pt-4">
                <h5 className="font-bold mb-1">Additional info</h5>
                <p className="text-sm text-base-frenchGray">
                  This information helps AI generate better offers.
                </p>
              </div>
              <hr className="border-base-ghostWhite" />

              <div className="grid grid-cols-1 gap-6">
                <div className="grid grid-cols-[180px_1fr] items-start gap-4">
                  <p className="uppercase text-xs text-base-frenchGray font-semibold">
                    Business category
                  </p>
                  <p className="text-base-raisinBlack">
                    {selectedCategoryLabel || '—'}
                  </p>
                </div>
                <div className="grid grid-cols-[180px_1fr] items-start gap-4">
                  <p className="uppercase text-xs text-base-frenchGray font-semibold">
                    Business subcategory
                  </p>
                  <p className="text-base-raisinBlack">
                    {display.subcategory?.length
                      ? display.subcategory.join(', ')
                      : '—'}
                  </p>
                </div>
                <hr className="border-base-ghostWhite" />

                <div className="grid grid-cols-[180px_1fr] items-start gap-4">
                  <p className="uppercase text-xs text-base-frenchGray font-semibold">
                    Business description
                  </p>
                  <p className="text-base-davyGray">
                    {display.description || '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <BusinessContacts email={display.email} phoneNumber={display.phoneNumber} />
        </div>
      </div>

      <BaseDialog open={open} onClose={() => setOpen(false)} size="max-w-3xl">
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex items-start justify-between">
          <div className="mr-auto flex items-start gap-3">
            <SuitcaseIcon width={41} height={41} />
            <div>
              <h5 className="font-bold text-lg leading-tight">
                Edit business information
              </h5>
              <p className="text-sm text-base-coolGray">
                Change the fields below to update the information about your
                business
              </p>
            </div>
          </div>
          <button type="button" onClick={() => setOpen(false)} className="ml-4">
            ✕
          </button>
        </div>
        <div>
          <HookedForm<BusinessFormValues>
            defaultValues={display}
            formHook={form}
            onSubmit={async (values) => {
              try {
                await mutateUpdateBusiness(values);
                if (pendingPhoto) {
                  await mutateUploadPhoto(pendingPhoto);
                }
                setDisplay(values);
                setOpen(false);
                toast.success('Business updated');
              } catch (e) {
                // errors are handled by mutations
              }
            }}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-4">
              <div className="px-6 pb-6">
                <div>
                  <div className="flex items-center gap-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (!file.type?.startsWith('image/')) {
                            toast.error('Please select an image file');
                            e.currentTarget.value = '';
                            return;
                          }
                          const maxSizeBytes = 3 * 1024 * 1024; // 3 MB
                          if (file.size > maxSizeBytes) {
                            toast.error('Logo must be 3 MB or smaller');
                            e.currentTarget.value = '';
                            return;
                          }
                          const url = URL.createObjectURL(file);
                          setLogoPreview(url);
                          setPendingPhoto(file);
                        }
                      }}
                    />
                    <div className="w-[96px] h-[96px] rounded-xl border border-dashed border-base-ghostWhite bg-base-seasalt flex items-center justify-center overflow-hidden">
                      {(() => {
                        const src =
                          logoPreview ||
                          (businessInfoResponse as any)?.data?.photo?.url ||
                          (NoLogo as string);
                        return (
                          <img
                            src={src}
                            className="w-full h-full object-cover"
                          />
                        );
                      })()}
                    </div>
                    <Button
                      type="button"
                      kind="secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose photo
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                  <InputField
                    name="name"
                    label="Business name"
                    placeholder=""
                  />
                  <InputField
                    name="email"
                    label="Business email"
                    type="email"
                    placeholder="contact@business.com"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mt-4">
                  <InputField
                    name="websiteUrl"
                    label="Website URL"
                    isOptional
                    placeholder="website.com"
                  />
                  <PhoneField
                    name="phoneNumber"
                    label="Phone Number"
                    isOptional
                  />
                </div>
                <div className="w-full mt-4">
                  <InputField
                    name="address"
                    label="Business address"
                    className="md:col-span-2"
                  />
                </div>
              </div>
            </div>
            <hr className="border-base-ghostWhite" />

            <div className="px-6 pb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
                <SelectField
                  name="categoryId"
                  label="Business category"
                  isOptional
                  placeholder="Select category"
                  options={(
                    (categoriesResponse?.categories ??
                      businessState?.categories) ||
                    []
                  ).map((c: { id: string; name: string }) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                  onSelect={(val) => {
                    setSelectedCategoryId(val as unknown as string);
                    form.setValue('subcategory', []);
                  }}
                />
                <MultipleSelectField
                  name="subcategory"
                  label="Business subcategories"
                  isOptional
                  allowCustomValues
                  placeholder="Select subcategories"
                  options={subcategoryOptions}
                  key={selectedCategoryId || 'no-category'}
                />
              </div>
              <TextField
                name="description"
                label="Business description"
                isOptional
                placeholder="Add additional business-related notes that will improve AI generation"
                maxLength={190}
                className="w-full"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                <Button
                  type="button"
                  kind="secondary"
                  className="w-full"
                  onClick={() => setOpen(false)}
                  disabled={isUpdating || isUploadingPhoto}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isUpdating || isUploadingPhoto}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </HookedForm>
        </div>
      </BaseDialog>
    </motion.div>
  );
};

export default BusinessDetails;
